import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel as InputLabelMUI,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { DigitalClock, LocalizationProvider, StaticDatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import cn from 'classnames';
import { addMinutes, format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { timezones } from '../../../../assets/data/timezones';
import { getDefaultExpirationDate } from '../../../../constants/bucket';
import { DateFormatsEnum, DEFAULT_TIMEZONE } from '../../../../constants/date';
import { ITimezone } from '../../../../types/timezone';
import {
  detectUserTimezone,
  formatDateToString,
  getMinTimeForSelectedDate,
  getRoundedMinDate,
} from '../../../../utils/date';
import { InputLabel } from '../../labels';

interface IDateAndTimezonePickerProps {
  className?: string;
  value?: string | null;
  onChange?: (value: { selectedDate: Date | null } | null) => void;
  minDate?: Date;
  maxDate?: Date;
  hint?: React.ReactNode;
  isLoading?: boolean;
  readOnly?: boolean;
  onSubmit?: (value: { selectedDate: Date | null; selectedDateString: string | null } | null) => void;
  defaultValue?: string | null;
  showLabel?: boolean;
  defaultView?: boolean;
  resetLabel?: string;
  label?: string;
  onReset?: () => void;
  isClearable?: boolean;
}

const TIME_PICKER_VIEWS = ['hours'] as const;

export const DateAndTimezonePicker: React.FC<IDateAndTimezonePickerProps> = ({
  className,
  value,
  onChange,
  minDate,
  maxDate,
  hint,
  isLoading = false,
  readOnly = false,
  defaultValue = null,
  onSubmit,
  showLabel = true,
  defaultView = false,
  resetLabel = 'Set to default',
  onReset,
  label = 'Select expiration',
  isClearable = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [timezone, setTimezone] = useState<ITimezone>(detectUserTimezone);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);

  const [dateToDisplay, setDateToDisplay] = useState<Date | null>(null);
  const [timeToDisplay, setTimeToDisplay] = useState<Date | null>(null);
  const [timezoneToDisplay, setTimezoneToDisplay] = useState<ITimezone>(detectUserTimezone);

  const hasValue = useMemo(() => selectedDate || time, [selectedDate, time]);

  const handleClear = useCallback(() => {
    if (readOnly) {
      return;
    }

    setSelectedDate(null);
    setTime(null);
    setDateToDisplay(null);
    setTimeToDisplay(null);
  }, [selectedDate, time, readOnly]);

  const triggerOnChange = useCallback(
    (newDate: Date | null, newTime: Date | null) => {
      if (readOnly) {
        return;
      }
      if (newDate && newTime) {
        newDate.setHours(newTime.getHours(), newTime.getMinutes());
        const utcDate = addMinutes(newDate, -(timezone.offset * 60));
        onChange?.({
          selectedDate: utcDate,
        });
      }
    },
    [onChange, readOnly, timezone.offset],
  );

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const submit = useCallback(() => {
    if (selectedDate && time) {
      setTimezoneToDisplay(timezone);
      selectedDate.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
      const utcDate = addMinutes(selectedDate, -(timezone.offset * 60));
      onSubmit?.({ selectedDate: utcDate, selectedDateString: formatDateToString(utcDate) });
    } else {
      onSubmit?.(null);
    }
  }, [onSubmit, selectedDate, time, timezone]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSubmit = useCallback(() => {
    submit();
    setAnchorEl(null);
  }, [submit]);

  const handleTimezoneChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      if (readOnly) {
        return;
      }
      const newTz = timezones.find((t) => t.value === event.target.value) || DEFAULT_TIMEZONE;
      setTimezone(newTz);
    },
    [readOnly],
  );

  const handleTimeChange = useCallback(
    (newTime: Date | null) => {
      if (readOnly) {
        return;
      }
      if (newTime) {
        newTime.setUTCMinutes(0, 0, 0);
        setTime(newTime);
        triggerOnChange(selectedDate, newTime);
      }
    },
    [readOnly, selectedDate, triggerOnChange],
  );

  const handleDateChange = useCallback(
    (newDate: Date | null) => {
      if (readOnly) {
        return;
      }
      setSelectedDate(newDate);
      triggerOnChange(newDate, time);
    },
    [readOnly, time, triggerOnChange],
  );

  const handleReset = useCallback(() => {
    if (readOnly) {
      return;
    }
    const defaultExpirationDate = getDefaultExpirationDate();
    setSelectedDate(defaultExpirationDate);

    const endTime = new Date();
    endTime.setHours(23, 59, 59);

    setTime(endTime);
    setTimezone(detectUserTimezone());
    onReset?.();
  }, [onReset, readOnly]);

  const formattedDateTime = useMemo(() => {
    if (!dateToDisplay || !timeToDisplay) {
      return label;
    }

    return `${format(toZonedTime(dateToDisplay, timezoneToDisplay.utc[0]), DateFormatsEnum.ddMMMyyyy)} ${format(
      timeToDisplay,
      DateFormatsEnum.TimeOnly,
    )} (${
      timezones.find(
        (t) =>
          t.value === timezoneToDisplay.value &&
          t.isdst === timezoneToDisplay.isdst &&
          t.utc.includes(timezoneToDisplay.utc[0]),
      )?.abbr
    })`;
  }, [dateToDisplay, timeToDisplay, timezoneToDisplay.isdst, timezoneToDisplay.utc, timezoneToDisplay.value]);

  const isTimeValid = useMemo(() => {
    // one-link mode
    if (isClearable) {
      if (!selectedDate && !time) {
        return true;
      }
    }

    // other modes
    if (!selectedDate || !time) {
      return false;
    }

    const minTime = getMinTimeForSelectedDate(selectedDate, timezone);
    if (!minTime) {
      return true;
    }

    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(time.getHours(), time.getMinutes(), 0, 0);

    return selectedDateTime.getTime() >= minTime.getTime();
  }, [isClearable, selectedDate, time, timezone]);

  const renderResetButton = useCallback(() => {
    if (isClearable) {
      return (
        hasValue && (
          <Button variant="text" onClick={handleClear} sx={{ mr: 'auto' }}>
            Clear
          </Button>
        )
      );
    }

    return (
      <Button variant="text" onClick={handleReset} sx={{ mr: 'auto' }}>
        {resetLabel}
      </Button>
    );
  }, [isClearable, handleClear, handleReset, hasValue, resetLabel]);

  useEffect(() => {
    const dateStr = value || defaultValue;

    if (dateStr) {
      const utcDate = parseISO(dateStr);
      const zoned = toZonedTime(utcDate, timezone.utc[0]);

      setSelectedDate(zoned);
      setDateToDisplay(zoned);

      const localTime = new Date();
      localTime.setHours(zoned.getHours(), zoned.getMinutes(), zoned.getSeconds(), 0);
      setTime(localTime);
      setTimeToDisplay(localTime);
    } else {
      setSelectedDate(null);
      setTime(null);

      setTimeToDisplay(null);
      setDateToDisplay(null);
    }
  }, [value, defaultValue]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className={cn(className)}>
        {showLabel && <InputLabel value="Expiration date" />}
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Box>
            <Button
              id="demo-positioned-button"
              onClick={handleClick}
              sx={{
                pl: 0,
                whiteSpace: 'nowrap',
                fontSize: '16px',
                fontWeight: 500,
                paddingLeft: 1,
                paddingRight: 0.25,
              }}
            >
              {formattedDateTime}
              <ArrowDropDownIcon />
            </Button>
            <Menu
              id="demo-positioned-menu"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={handleClose}
            >
              <Box sx={{ width: 320, paddingTop: '15px', mx: 'auto' }}>
                <FormControl fullWidth sx={{ px: 2 }}>
                  <InputLabelMUI sx={{ pl: 2 }} id="timezone-label">
                    Timezone
                  </InputLabelMUI>
                  <Select
                    labelId="timezone-label"
                    value={timezone.value}
                    label="Timezone"
                    onChange={handleTimezoneChange}
                    disabled={readOnly}
                    fullWidth
                  >
                    {timezones.map((tz: ITimezone) => (
                      <MenuItem key={tz.offset + tz.value} value={tz.value}>
                        {tz.text}
                      </MenuItem>
                    ))}
                  </Select>

                  <TimePicker
                    viewRenderers={{
                      hours: (props) => {
                        return (
                          <DigitalClock
                            ampm={false}
                            timeStep={60}
                            value={time}
                            minTime={getMinTimeForSelectedDate(selectedDate, timezone)}
                            onChange={handleTimeChange}
                            skipDisabled={true}
                            slots={{
                              digitalClockItem: (itemProps) => {
                                const hourValue = itemProps.children.split(':')[0];
                                const formattedHour = `${hourValue?.toString().padStart(2, '0')}:00`;

                                return (
                                  <MenuItem
                                    {...itemProps}
                                    selected={itemProps.selected}
                                    onClick={() => itemProps.onClick?.(itemProps.value)}
                                  >
                                    {formattedHour}
                                  </MenuItem>
                                );
                              },
                            }}
                          />
                        );
                      },
                    }}
                    ampm={false}
                    value={time}
                    onChange={handleTimeChange}
                    sx={{ width: '100%', mt: 2 }}
                    format="HH:00"
                    views={TIME_PICKER_VIEWS}
                    disabled={readOnly}
                    minTime={getMinTimeForSelectedDate(selectedDate, timezone)}
                    slotProps={{
                      textField: {
                        inputProps: {
                          readOnly: true,
                        },
                      },
                    }}
                  />
                </FormControl>

                <Box sx={{ mt: 2 }}>
                  <StaticDatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    minDate={minDate || getRoundedMinDate(timezone)}
                    maxDate={maxDate}
                    readOnly={readOnly}
                    referenceDate={defaultView && selectedDate ? selectedDate : undefined}
                    slotProps={{
                      layout: { sx: { width: '100%' } },
                    }}
                    slots={{
                      toolbar: () => null,
                      actionBar: () => null,
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, px: 2 }}>
                  {renderResetButton()}
                  <Button variant="contained" onClick={handleSubmit} disabled={!isTimeValid || readOnly}>
                    OK
                  </Button>
                </Box>
              </Box>
            </Menu>
          </Box>
        )}
        {hint && <Typography variant="caption">{hint}</Typography>}
      </Box>
    </LocalizationProvider>
  );
};
